import React from "react";
import {
  RefreshIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import Button from "../../../components/common/Button";

const RefundMethodModal = ({
  order,
  refundMethod,
  setRefundMethod,
  refundBankDetails,
  setRefundBankDetails,
  refundUpiDetails,
  setRefundUpiDetails,
  onConfirm,
  onCancel,
  formatCurrency,
}) => (
  <div className="space-y-4">
    {/* Alert */}
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <RefreshIcon className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-orange-900 mb-1">
            Refund Required Before Confirmation
          </p>
          <p className="text-sm text-orange-800">
            This order has a refund due of{" "}
            <strong className="text-orange-900">
              {formatCurrency(order.refundDetails?.refundAmount || 0)}
            </strong>
            . Please specify how the refund will be processed.
          </p>
        </div>
      </div>
    </div>

    {/* Refund Method Selection */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Select Refund Method *
      </label>
      <div className="space-y-2">
        {/* Cash */}
        <button
          onClick={() => setRefundMethod("CASH")}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
            refundMethod === "CASH"
              ? "border-green-500 bg-green-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div
            className={`p-1.5 rounded-lg ${
              refundMethod === "CASH" ? "bg-green-100" : "bg-gray-100"
            }`}
          >
            <CurrencyRupeeIcon
              className={`w-4 h-4 ${
                refundMethod === "CASH" ? "text-green-600" : "text-gray-400"
              }`}
            />
          </div>
          <div className="flex-1 text-left">
            <span
              className={`font-medium text-sm ${
                refundMethod === "CASH" ? "text-green-700" : "text-gray-700"
              }`}
            >
              Cash Refund
            </span>
            <p className="text-xs text-gray-600">Refund in cash at store</p>
          </div>
          {refundMethod === "CASH" && (
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
          )}
        </button>

        {/* Bank Transfer */}
        <button
          onClick={() => setRefundMethod("BANK_TRANSFER")}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
            refundMethod === "BANK_TRANSFER"
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div
            className={`p-1.5 rounded-lg ${
              refundMethod === "BANK_TRANSFER" ? "bg-blue-100" : "bg-gray-100"
            }`}
          >
            <DocumentTextIcon
              className={`w-4 h-4 ${
                refundMethod === "BANK_TRANSFER"
                  ? "text-blue-600"
                  : "text-gray-400"
              }`}
            />
          </div>
          <div className="flex-1 text-left">
            <span
              className={`font-medium text-sm ${
                refundMethod === "BANK_TRANSFER"
                  ? "text-blue-700"
                  : "text-gray-700"
              }`}
            >
              Bank Transfer
            </span>
            <p className="text-xs text-gray-600">
              Transfer to customer's account
            </p>
          </div>
          {refundMethod === "BANK_TRANSFER" && (
            <CheckCircleIcon className="w-5 h-5 text-blue-600" />
          )}
        </button>

        {/* UPI */}
        <button
          onClick={() => setRefundMethod("UPI")}
          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center gap-3 ${
            refundMethod === "UPI"
              ? "border-purple-500 bg-purple-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div
            className={`p-1.5 rounded-lg ${
              refundMethod === "UPI" ? "bg-purple-100" : "bg-gray-100"
            }`}
          >
            <CurrencyRupeeIcon
              className={`w-4 h-4 ${
                refundMethod === "UPI" ? "text-purple-600" : "text-gray-400"
              }`}
            />
          </div>
          <div className="flex-1 text-left">
            <span
              className={`font-medium text-sm ${
                refundMethod === "UPI" ? "text-purple-700" : "text-gray-700"
              }`}
            >
              UPI Transfer
            </span>
            <p className="text-xs text-gray-600">Send via UPI</p>
          </div>
          {refundMethod === "UPI" && (
            <CheckCircleIcon className="w-5 h-5 text-purple-600" />
          )}
        </button>
      </div>
    </div>

    {/* Bank Details Form */}
    {refundMethod === "BANK_TRANSFER" && (
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-3">
        <p className="text-sm font-medium text-blue-900 mb-2">
          Bank Account Details
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Account Holder Name *
          </label>
          <input
            type="text"
            value={refundBankDetails.accountName}
            onChange={(e) =>
              setRefundBankDetails({
                ...refundBankDetails,
                accountName: e.target.value,
              })
            }
            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Enter account holder name"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Account Number *
          </label>
          <input
            type="text"
            value={refundBankDetails.accountNumber}
            onChange={(e) =>
              setRefundBankDetails({
                ...refundBankDetails,
                accountNumber: e.target.value,
              })
            }
            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Enter account number"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              IFSC Code *
            </label>
            <input
              type="text"
              value={refundBankDetails.ifscCode}
              onChange={(e) =>
                setRefundBankDetails({
                  ...refundBankDetails,
                  ifscCode: e.target.value.toUpperCase(),
                })
              }
              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm uppercase"
              placeholder="IFSC"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Bank Name *
            </label>
            <input
              type="text"
              value={refundBankDetails.bankName}
              onChange={(e) =>
                setRefundBankDetails({
                  ...refundBankDetails,
                  bankName: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Bank name"
            />
          </div>
        </div>
      </div>
    )}

    {/* UPI Details Form */}
    {refundMethod === "UPI" && (
      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 space-y-3">
        <p className="text-sm font-medium text-purple-900 mb-2">UPI Details</p>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            UPI ID *
          </label>
          <input
            type="text"
            value={refundUpiDetails.vpa}
            onChange={(e) =>
              setRefundUpiDetails({
                ...refundUpiDetails,
                vpa: e.target.value,
              })
            }
            className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            placeholder="example@upi"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Account Holder Name (Optional)
          </label>
          <input
            type="text"
            value={refundUpiDetails.verifiedName}
            onChange={(e) =>
              setRefundUpiDetails({
                ...refundUpiDetails,
                verifiedName: e.target.value,
              })
            }
            className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            placeholder="Enter name"
          />
        </div>
      </div>
    )}

    {/* Actions */}
    <div className="flex justify-end gap-2 pt-2">
      <Button variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onConfirm}>
        Confirm Order with Refund Method
      </Button>
    </div>
  </div>
);

export default RefundMethodModal;
